import { getScalpMeasureItemAppendix } from "@becon-team/becon-fe-lib/utils";
import { ReactNode } from "react";

import { SkinType, SkinIssue } from "api/@skin/skin.dto";

import type { TFunction } from "i18next";
import {
  Acne,
  BlackHead,
  DarkCircle,
  DryDeadSkin,
  Dryness,
  Inflammation,
  Mole,
  NumberOfPores,
  Pigmentation,
  PoreRatio,
  Porphyrin,
  Redness,
  Sebum,
  Temperature,
  Water,
  Wrinkle,
} from "@becon-team/becon-fe-lib/components";
import { BeconScalpTypes, BeconSkinTypes } from "@becon-team/becon-fe-lib/my-types";

export const getSkinMeasureItemData = (
  t: TFunction,
  skinMeasureItem: BeconSkinTypes.SkinMeasureItem,
): {
  text: string;
  icon: React.ReactNode;
  unit: string;
} => {
  switch (skinMeasureItem) {
    case "PORE_DENSITY":
      return { text: t("모공 밀도"), icon: <PoreRatio />, unit: t("개/cm²") };
    case "PORE_SIZE":
      return { text: t("모공 크기"), icon: <NumberOfPores />, unit: "mm²" };
    case "SEBUM":
      return { text: t("피지"), icon: <Sebum />, unit: "%" };
    case "SENSITIVITY":
      return { text: t("홍조"), icon: <Redness />, unit: "%" };
    case "MOISTURE":
      return { text: t("수분"), icon: <Water />, unit: t("...점") };
    case "TEMPERATURE":
      return { text: t("피부열"), icon: <Temperature />, unit: "˚C" };
    case "ACNE":
      return { text: t("여드름"), icon: <Acne />, unit: "%" };
    case "DARK_CIRCLE":
      return { text: t("다크서클"), icon: <Mole />, unit: "%" };
    case "WRINKLE":
      return { text: t("주름"), icon: <Wrinkle />, unit: "%" };
    case "PORPHYRIN":
      return { text: t("포피린"), icon: <Porphyrin />, unit: "%" };
    case "PIGMENTATION":
      return { text: t("기미/잡티"), icon: <Pigmentation />, unit: "%" };
    case "DEAD_SKIN":
      return { text: "", icon: null, unit: "" };
    case "GAS":
      return { text: "", icon: <Pigmentation />, unit: "" };
    default:
      return { text: "", icon: <Pigmentation />, unit: "" };
  }
};

export const getUserSkinTypeText = (t: TFunction, skinType: SkinType): string => {
  switch (skinType) {
    case "GENERAL":
      return t("중성");
    case "OILY":
      return t("지성");
    case "DRY":
      return t("건성");
    case "TROUBLE":
      return t("트러블성");
    case "SENSITIVITY":
      return t("민감성");
    case "COMBINATION":
      return t("복합성");
  }
};

export const getSkinMeasureItemAppendix = (
  t: TFunction,
  skinMeasureItem: BeconSkinTypes.SkinMeasureItem,
):
  | {
      info: string;
      images: {
        text: string;
        imageUrl: string;
      }[];
      sensorInfo?: string;
    }
  | undefined => {
  switch (skinMeasureItem) {
    case "PORE_DENSITY":
      return {
        info: t(
          "모공 밀도가 높은 피부는 피지와 노폐물이 쌓이기 쉬워 여드름이나 피부 트러블의 원인이 될 수 있습니다. 높은 모공 밀도는 피부의 외관을 거칠게 만들며, 메이크업이나 스킨케어 제품의 흡수율을 감소시킬 수 있습니다. 반면, 적절한 관리를 통해 모공의 크기와 밀도를 조절하면 피부의 질감을 개선하고 더 매끄러운 외관을 유지할 수 있습니다. 모공 밀도가 낮은 피부는 통풍이 잘되어 피부 트러블이 덜 발생하는 경향이 있습니다.",
        ),
        images: [
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
        ],
      };
    case "PORE_SIZE":
      return {
        info: t(
          "모공 크기는 피부의 전반적인 질감과 외관에 큰 영향을 미칩니다. 큰 모공은 피부가 더 거칠어 보이게 하며, 메이크업 시에도 불균일하게 보일 수 있습니다. 모공 크기가 크면 피지 분비가 과도하게 이루어져 피부 문제를 유발할 수 있습니다. 반면, 작은 모공은 피부가 더 매끄럽고 깨끗한 외관을 유지하는 데 도움이 되며, 여드름과 같은 피부 문제의 발생률을 낮출 수 있습니다.",
        ),
        images: [
          {
            text: "mm",
            imageUrl: "",
          },
          {
            text: "mm",
            imageUrl: "",
          },
          {
            text: "mm",
            imageUrl: "",
          },
        ],
      };
    case "SEBUM":
      return {
        info: t(
          "피지는 피부를 보호하고 유연하게 유지하는 데 필수적이지만, 과도한 피지 분비는 여드름 발생의 주요 원인 중 하나입니다. 피지가 과도하게 분비되면 모공을 막아 피부 트러블을 유발할 수 있으며, 피부가 번들거리고 기름진 느낌을 줄 수 있습니다. 적절한 피지 조절은 피부의 밸런스를 유지하고 여드름이나 피부 트러블을 예방하는 데 중요합니다.",
        ),
        images: [
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
        ],
      };
    case "SENSITIVITY":
      return {
        info: t(
          "피부 민감도가 높은 사람들은 외부 자극에 대해 쉽게 반응하여 발적, 가려움, 염증 등을 경험할 수 있습니다. 민감한 피부를 가진 사람들은 자극적인 성분이 없는 순한 스킨케어 제품을 사용해야 하며, 피부 보호막을 강화하고 진정시키는 성분이 포함된 제품을 선택하는 것이 좋습니다.",
        ),
        images: [
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
        ],
      };

    case "ACNE":
      return {
        info: t(
          "여드름은 피부의 염증성 질환으로, 피부의 외관뿐만 아니라 자존감에도 큰 영향을 미칠 수 있습니다. 여드름의 적절한 관리와 치료는 피부의 건강을 개선하고 장기적인 흉터 형성을 방지하는 데 중요합니다.",
        ),
        images: [
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
        ],
      };
    case "PIGMENTATION":
      return {
        info: t(
          "피부의 색소 침착 문제와 흉터는 피부의 전반적인 외관에 영향을 미치며, 자신감 감소와 같은 심리적 영향을 줄 수 있습니다. 적절한 색소 침착 및 흉터 치료는 피부의 균일한 톤을 회복하고, 건강하고 매끄러운 피부를 유지하는 데 도움이 됩니다.",
        ),
        images: [
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
        ],
      };
    case "DARK_CIRCLE":
      return {
        info: t(
          "다크서클은 피로 또는 수면 부족의 징후일 수 있으며, 피부 아래 혈관이 더 분명하게 보이게 함으로써 피부가 더 어둡게 보이게 만듭니다. 다크서클을 줄이는 것은 피부의 밝고 건강한 외관을 회복하는 데 도움이 됩니다.",
        ),
        images: [
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
        ],
      };
    case "WRINKLE":
      return {
        info: t(
          "주름은 피부 노화의 자연스러운 일부이지만, 환경적 요인과 생활 습관에 의해 가속화될 수 있습니다. 주름의 관리는 피부의 탄력과 젊음을 유지하는 데 중요하며, 피부 노화를 지연시킬 수 있습니다.",
        ),
        images: [
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
        ],
      };

    case "PORPHYRIN":
      return {
        info: t(
          "포피린은 피부에 존재하는 자연 발생 화합물로, 과도하게 축적되면 피부 문제의 원인이 될 수 있습니다. 포피린 수치가 높은 피부는 여드름 및 기타 피부 질환에 더 취약할 수 있으며, 이는 피부의 세균 증식과 관련이 있을 수 있습니다.",
        ),
        images: [
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
          {
            text: "%",
            imageUrl: "",
          },
        ],
      };
    case "TEMPERATURE":
      return {
        info: t(
          "피부 온도는 피부의 건강 상태와 밀접하게 관련되어 있습니다. 높은 피부 온도는 염증 반응을 나타낼 수 있으며, 피부 질환의 존재를 시사할 수 있습니다. 피부 온도를 정상 범위 내로 유지하는 것은 피부의 건강을 유지하고 염증을 예방하는 데 중요합니다.",
        ),
        images: [],
        sensorInfo: getScalpMeasureItemAppendix(t, BeconScalpTypes.ScalpMeasureItemEnum.TEMPERATURE).sensorInfo,
      };

    case "MOISTURE":
      return {
        info: t(
          "피부의 수분 수준은 피부의 탄력성과 유연성을 결정하는 중요한 요소입니다. 수분이 부족한 피부는 탄력을 잃고 주름이 생기기 쉬우며, 피부의 방어 기능도 약화됩니다. 적절한 수분 공급은 피부를 건강하고 생기 있게 유지하는 데 필수적이며, 피부 노화를 지연시키는 효과가 있습니다.",
        ),
        images: [],
        sensorInfo: getScalpMeasureItemAppendix(t, BeconScalpTypes.ScalpMeasureItemEnum.WATER).sensorInfo,
      };
  }
};

export const getSkinIssueData = (
  t: TFunction,
  skinIssue: SkinIssue,
): {
  text: string;
  icon?: ReactNode;
} => {
  switch (skinIssue) {
    case "NONE":
      return { text: t("없음") };
    case "WRINKLE":
      return { text: t("주름"), icon: <Wrinkle /> };
    case "DARK_CIRCLE":
      return { text: t("다크서클"), icon: <DarkCircle /> };
    case "ELASTICITY":
      return { text: t("탄력 저하"), icon: <Water /> };
    case "BLEMISHES":
      return { text: t("기미/잡티"), icon: <NumberOfPores /> };
    case "ACNE":
      return { text: t("여드름/트러블"), icon: <Acne /> };
    case "ACNE_SCAR":
      return { text: t("여드름 흉터"), icon: <Inflammation /> };
    case "PORE_SIZE":
      return { text: t("모공 크기"), icon: <PoreRatio /> };
    case "DEAD_SKIN":
      return { text: t("각질"), icon: <DryDeadSkin /> };
    case "DRY":
      return { text: t("건조"), icon: <Dryness /> };
    case "ITCH":
      return { text: t("가려움"), icon: <Pigmentation /> };
    case "SEBUM":
      return { text: t("피지 과다"), icon: <Sebum /> };
    case "BLACKHEAD":
      return { text: t("블랙헤드"), icon: <BlackHead /> };
    case "SENSITIVITY":
      return { text: t("민감 피부"), icon: <Redness /> };
    case "WHITENING":
      return { text: t("미백"), icon: <Wrinkle /> };
  }
};

export const getSkinMacroPositionText = (t: TFunction, microPosition: BeconSkinTypes.SkinMacroPosition): string => {
  switch (microPosition) {
    case "CENTER":
      return t("정면");
    case "SIDE_LEFT":
      return t("좌측");
    case "SIDE_RIGHT":
      return t("우측");
    default:
      return "";
  }
};

export const getSkinMicroPositionText = (t: TFunction, microPosition: BeconSkinTypes.SkinMicroPosition): string => {
  switch (microPosition) {
    case "NOSE":
      return t("코");
    case "CHEEK":
      return t("볼");
    case "CHIN":
      return t("턱");
    case "FOREHEAD":
      return t("이마");
    default:
      return "";
  }
};

export const getSkinMicroPositionSnakeText = (
  t: TFunction,
  microPosition: "nose" | "cheek" | "chin" | "forehead",
): string => {
  switch (microPosition) {
    case "nose":
      return t("코");
    case "cheek":
      return t("볼");
    case "chin":
      return t("턱");
    case "forehead":
      return t("이마");
  }
};
